import React, { useMemo, useState, useEffect } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Truck, Store, MapPin, CheckCircle2, Loader2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const dateOptions = () => {
  const out = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push({ value: d.toISOString().slice(0, 10), label: i === 0 ? "Today" : i === 1 ? "Tomorrow" : d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) });
  }
  return out;
};
const TIMES = ["11:30 AM", "12:00 PM", "12:30 PM", "1:00 PM", "5:00 PM", "5:30 PM", "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM"];
const TIPS = [0.15, 0.18, 0.2];

export default function CartDrawer() {
  const { items, removeItem, updateQty, subtotal, open, setOpen, clear } = useCart();
  const { settings } = useData();
  const { user } = useAuth();
  const [mode, setMode] = useState("pickup");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", zip: "", date: "", time: "", notes: "" });
  const [tipPct, setTipPct] = useState(0.18);
  const [customTip, setCustomTip] = useState("");
  const [zoneInfo, setZoneInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loyalty, setLoyalty] = useState(null);
  const [redeemLoyalty, setRedeemLoyalty] = useState(false);

  const loyCfg = settings?.loyalty || {};
  const plateCats = loyCfg.plate_categories || [];
  const plateItems = items.filter((i) => plateCats.includes(i.category_id));
  const canRedeem = !!(loyCfg.enabled && loyalty?.rewards_available > 0 && plateItems.length);

  useEffect(() => {
    const digits = (form.phone || "").replace(/\D/g, "");
    if (loyCfg.enabled && digits.length >= 10) {
      api.get(`/loyalty/${digits}`).then((r) => setLoyalty(r.data)).catch(() => setLoyalty(null));
    } else {
      setLoyalty(null);
    }
    // eslint-disable-next-line
  }, [form.phone, settings]);

  const taxRate = Number(settings?.tax_rate || 0);
  const deliveryFee = mode === "delivery" ? Number(zoneInfo?.available ? zoneInfo.fee : settings?.delivery_fee || 0) : 0;
  const tip = customTip !== "" ? Number(customTip) || 0 : subtotal * tipPct;
  const tax = subtotal * taxRate;
  const loyaltyDiscount = redeemLoyalty && canRedeem ? Math.min(...plateItems.map((i) => i.basePrice ?? i.unitPrice)) : 0;
  const total = Math.max(subtotal + tax + deliveryFee + tip - loyaltyDiscount, 0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        phone: f.phone || user.phone || "",
        email: f.email || user.email || "",
        address: f.address || user.address || "",
        zip: f.zip || user.zip_code || "",
      }));
    }
    // eslint-disable-next-line
  }, [open, user]);

  const checkZone = async () => {
    if (!form.zip && !form.address) return toast.error("Enter your ZIP code or town");
    setChecking(true);
    try {
      const r = await api.post("/delivery/check", { query: form.zip || form.address });
      setZoneInfo(r.data);
      if (r.data.available) toast.success(`We deliver to ${r.data.town}!`);
      else toast.error(r.data.message);
    } finally { setChecking(false); }
  };

  const canSubmit = items.length && form.name && form.phone && form.date && form.time &&
    (mode === "pickup" || (mode === "delivery" && zoneInfo?.available && form.address));

  const checkout = async () => {
    if (!canSubmit) return toast.error("Please complete the required fields");
    setSubmitting(true);
    try {
      const orderRes = await api.post("/orders", {
        items: items.map((i) => ({
          item_id: i.item_id, quantity: i.quantity, spice_level: i.spice_level,
          selected_option: i.selected_option, selected_flavor: i.selected_flavor,
          selected_rice: i.selected_rice, selected_sides: i.selected_sides,
          addons: i.addons, special_instructions: i.special_instructions,
        })),
        fulfillment_type: mode,
        customer_name: form.name, customer_phone: form.phone,
        customer_email: form.email || null,
        address: mode === "delivery" ? form.address : null,
        zip_code: mode === "delivery" ? form.zip : null,
        scheduled_time: `${form.date} ${form.time}`,
        tip: Number(tip.toFixed(2)),
        redeem_loyalty: redeemLoyalty && canRedeem,
        special_instructions: form.notes,
      });
      const order = orderRes.data;
      localStorage.setItem("skk_last_order", order.order_number);
      const pay = await api.post("/payments/checkout", {
        order_id: order.order_id,
        origin_url: window.location.origin,
      });
      clear();
      window.location.href = pay.data.checkout_url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#101311] border-emerald-800/50 text-[#FDFBF7] w-full sm:max-w-md p-0 flex flex-col">
        <div className="p-5 border-b border-emerald-800/50 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-amber-400" />
          <span className="font-display font-black text-xl uppercase tracking-wide">Your Order</span>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div>
              <p className="text-[#FDFBF7]/60 mb-4">Your cart is empty.</p>
              <button onClick={() => setOpen(false)} className="px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Browse Menu</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar p-5 space-y-4">
              {items.map((i) => (
                <div key={i.cartId} data-testid={`cart-line-${i.item_id}`} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <span className="font-display font-bold leading-tight">{i.name}</span>
                      <span className="text-amber-400 font-bold">{money(i.lineTotal)}</span>
                    </div>
                    {i.spice_level && <div className="text-xs text-red-400">{i.spice_level}</div>}
                    {i.selected_option && <div className="text-xs text-amber-400/90">{i.selected_option}</div>}
                    {i.selected_flavor && <div className="text-xs text-amber-400/90">{i.selected_flavor}</div>}
                    {i.selected_rice && <div className="text-xs text-[#FDFBF7]/50">Rice: {i.selected_rice}</div>}
                    {i.selected_sides?.length > 0 && <div className="text-xs text-[#FDFBF7]/50">Sides: {i.selected_sides.join(", ")}</div>}
                    {i.addons?.length > 0 && <div className="text-xs text-[#FDFBF7]/50">+ {i.addons.join(", ")}</div>}
                    {i.special_instructions && <div className="text-xs text-[#FDFBF7]/50 italic">"{i.special_instructions}"</div>}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-2 bg-[#181D1A] rounded-full border border-emerald-800/50 px-1.5 py-1">
                        <button onClick={() => updateQty(i.cartId, i.quantity - 1)} className="w-9 h-9 grid place-items-center active:scale-90 transition-transform"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm w-5 text-center font-bold">{i.quantity}</span>
                        <button onClick={() => updateQty(i.cartId, i.quantity + 1)} className="w-9 h-9 grid place-items-center active:scale-90 transition-transform"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`cart-remove-${i.item_id}`} onClick={() => removeItem(i.cartId)} className="text-[#FDFBF7]/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="grid grid-cols-2 gap-2 pt-2">
                <button data-testid="checkout-pickup-tab" onClick={() => setMode("pickup")} className={`flex items-center justify-center gap-2 py-3 rounded-xl font-display font-bold uppercase text-sm border transition-colors ${mode === "pickup" ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>
                  <Store className="w-4 h-4" /> Pickup
                </button>
                <button data-testid="checkout-delivery-tab" onClick={() => setMode("delivery")} disabled={settings && settings.delivery_enabled === false} className={`flex items-center justify-center gap-2 py-3 rounded-xl font-display font-bold uppercase text-sm border transition-colors disabled:opacity-40 ${mode === "delivery" ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50 text-[#FDFBF7]/80"}`}>
                  <Truck className="w-4 h-4" /> Delivery
                </button>
              </div>

              {mode === "delivery" && (
                <div className="space-y-2 rounded-xl bg-[#181D1A] p-3 border border-emerald-800/40">
                  <input data-testid="checkout-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Street address" className="w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
                  <div className="flex gap-2">
                    <input data-testid="delivery-zip-checker-input" inputMode="numeric" autoComplete="postal-code" value={form.zip} onChange={(e) => set("zip", e.target.value)} placeholder="ZIP or town" className="flex-1 bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
                    <button data-testid="delivery-zip-checker-submit" onClick={checkZone} className="px-4 rounded-lg bg-emerald-700 hover:bg-emerald-600 font-bold text-sm flex items-center gap-1">
                      {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Check
                    </button>
                  </div>
                  {zoneInfo && (
                    <div className={`text-xs ${zoneInfo.available ? "text-emerald-400" : "text-red-400"}`}>
                      {zoneInfo.available ? `✓ ${zoneInfo.town} · fee ${money(zoneInfo.fee)} · ${zoneInfo.eta} · min ${money(zoneInfo.min_order)}` : zoneInfo.message}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <select data-testid="checkout-date-select" value={form.date} onChange={(e) => set("date", e.target.value)} className="bg-[#181D1A] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400">
                  <option value="">Select date</option>
                  {dateOptions().map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
                <select data-testid="checkout-time-select" value={form.time} onChange={(e) => set("time", e.target.value)} className="bg-[#181D1A] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400">
                  <option value="">Select time</option>
                  {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              <input data-testid="checkout-name-input" autoComplete="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name *" className="w-full bg-[#181D1A] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="checkout-phone-input" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone *" className="bg-[#181D1A] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
                <input data-testid="checkout-email-input" type="email" autoComplete="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email (for receipt)" className="bg-[#181D1A] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400" />
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-2">Add a Tip</div>
                <div className="flex gap-2">
                  {TIPS.map((t) => (
                    <button key={t} onClick={() => { setTipPct(t); setCustomTip(""); }} className={`flex-1 py-2 rounded-lg text-sm font-bold border ${customTip === "" && tipPct === t ? "bg-amber-400 text-[#0A2818] border-amber-400" : "border-emerald-800/50"}`}>{t * 100}%</button>
                  ))}
                  <input value={customTip} onChange={(e) => setCustomTip(e.target.value)} placeholder="$" className="w-16 bg-[#181D1A] rounded-lg px-2 py-2 text-sm border border-emerald-800/50 text-center outline-none focus:border-amber-400" />
                </div>
              </div>

              {loyCfg.enabled && (
                <div className="rounded-xl bg-[#181D1A] border border-amber-500/30 p-3">
                  <div className="text-xs uppercase tracking-wider text-amber-400 font-bold mb-1 flex items-center gap-2"><i className="fa-solid fa-award" /> Sir. King Rewards</div>
                  {loyalty ? (
                    loyalty.rewards_available > 0 ? (
                      <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid="loyalty-redeem" checked={redeemLoyalty} onChange={(e) => setRedeemLoyalty(e.target.checked)} disabled={!plateItems.length} className="accent-amber-400 w-4 h-4" />
                        🎉 {loyalty.rewards_available} free plate reward{loyalty.rewards_available > 1 ? "s" : ""} available — apply one{!plateItems.length ? " (add a plate)" : ""}!
                      </label>
                    ) : (
                      <div className="text-sm text-[#FDFBF7]/70">{loyalty.plates % loyalty.threshold}/{loyalty.threshold} plates toward a free one. Keep going!</div>
                    )
                  ) : (
                    <div className="text-sm text-[#FDFBF7]/50">Enter your phone above to see your rewards.</div>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-emerald-800/50 p-5 space-y-1.5">
              <Row label="Subtotal" value={money(subtotal)} />
              <Row label={`Tax${taxRate ? ` (${(taxRate * 100).toFixed(1)}%)` : ""}`} value={money(tax)} />
              {mode === "delivery" && <Row label="Delivery" value={money(deliveryFee)} />}
              <Row label="Tip" value={money(tip)} />
              {loyaltyDiscount > 0 && <Row label="Loyalty reward" value={`-${money(loyaltyDiscount)}`} />}
              <div className="flex justify-between font-display font-black text-amber-400 text-lg pt-1">
                <span>Total</span><span data-testid="checkout-total">{money(total)}</span>
              </div>
              <button
                data-testid="checkout-submit-button"
                onClick={checkout}
                disabled={!canSubmit || submitting}
                className="w-full mt-3 py-4 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform"
              >
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> : <><CheckCircle2 className="w-5 h-5" /> Pay Online · {money(total)}</>}
              </button>
              <p className="text-[10px] text-center text-[#FDFBF7]/40 pt-1">Secure checkout via Stripe · Test card 4242 4242 4242 4242</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm text-[#FDFBF7]/75"><span>{label}</span><span>{value}</span></div>
);
